import { EntityInterface } from './Table';

/**
 * WalletInterface defines the structure of a wallet object from the backend API
 */
export interface WalletInterface {
    achwalletID: string;
    nickname: string;
    accountNumber: string;
    accountType: number;
    abaNumber: string;
    bankName: string;
    validated: string;
    active: string;
    operation?: string;
}

/**
 * Wallet class representing a wallet with specialized getters and methods
 */
export class Wallet implements EntityInterface {
    /**
     * Constructor for the Wallet class
     * 
     * @param achwalletID Unique identifier for the wallet
     * @param nickname User-friendly name for the wallet
     * @param accountNumber Bank account number
     * @param accountType Account type (0 = Checking, 1 = Savings)
     * @param abaNumber Routing number (ABA number)
     * @param bankName Name of the bank
     * @param validated Whether the account is validated
     * @param active Whether the account is active
     */
    constructor(
        public achwalletID: string,
        public nickname: string,
        public accountNumber: string,
        public accountType: number,
        public abaNumber: string,
        public bankName: string,
        public validated: string,
        public active: string,
    ) { }

    /**
     * EntityInterface implementation - returns the unique identifier
     */
    get id(): string {
        return this.achwalletID;
    }

    /**
     * Creates a copy of the current Wallet instance
     * 
     * @returns A copy of the current Wallet instance
     */
    copyOf(): Wallet {
        return new Wallet(
            this.achwalletID,
            this.nickname,
            this.accountNumber,
            this.accountType,
            this.abaNumber,
            this.bankName,
            this.validated,
            this.active,
        );
    }

    /**
     * Return true if the current Wallet instance is equal to the given wallet
     * 
     * @param other 
     * @returns 
     */
    equals(other: Wallet): boolean {
        return this.achwalletID === other.achwalletID &&
               this.nickname === other.nickname &&
               this.accountNumber === other.accountNumber &&
               this.accountType === other.accountType &&
               this.abaNumber === other.abaNumber &&
               this.bankName === other.bankName &&
               this.validated === other.validated &&
               this.active === other.active;
    }

    /**
     * Check if the wallet is pending (has [p] in nickname)
     */
    get isPending(): boolean {
        return this.nickname.includes('[p]');
    }

    /**
     * Check if the wallet is active
     */
    get isActive(): boolean {
        return this.active === '1';
    }

    /**
     * Check if the wallet is validated
     */
    get isValidated(): boolean {
        return this.validated === '1';
    }

    /**
     * Get account type as string
     */
    get accountTypeString(): string {
        return this.accountType === 0 ? 'Checking' : 'Savings';
    }

    /**
     * Get masked account number (showing only last 4 digits)
     */
    get maskedAccountNumber(): string {
        if (this.accountNumber.length <= 4) {
            return this.accountNumber;
        }
        return '*'.repeat(this.accountNumber.length - 4) + this.accountNumber.slice(-4);
    }

    /**
     * Get display status for the wallet
     */
    get status(): string {
        if (this.isPending) {
            return 'Pending';
        }
        return this.isActive ? 'Active' : 'Inactive';
    }
}

/**
 * WalletMapper provides methods to convert between Wallet instances and API interfaces
 */
export class WalletMapper {
    /**
     * Convert from API interface to Wallet model
     * 
     * @param walletInterface 
     * @returns 
     */
    static toModel(walletInterface: WalletInterface): Wallet {
        return new Wallet(
            walletInterface.achwalletID,
            walletInterface.nickname,
            walletInterface.accountNumber,
            walletInterface.accountType,
            walletInterface.abaNumber,
            walletInterface.bankName,
            walletInterface.validated,
            walletInterface.active,
        );
    }

    /**
     * Convert from Wallet model to API interface
     * 
     * @param wallet 
     * @returns 
     */
    static toInterface(wallet: Wallet): WalletInterface {
        return {
            achwalletID: wallet.achwalletID,
            nickname: wallet.nickname,
            accountNumber: wallet.accountNumber,
            accountType: wallet.accountType,
            abaNumber: wallet.abaNumber,
            bankName: wallet.bankName,
            validated: wallet.validated,
            active: wallet.active,
        };
    }
}
